import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import { Globals } from "../globals";
import { loadGemeindeSettings, saveGemeindeSettings } from "../services/gemeindeSettingsService";
import { useAuth } from "../hooks/useAuth";
import { useGemeinde } from "../context/GemeindeContext";
import I_IMC_Props from "../infos/I_IMC_Props";
import Card from "react-bootstrap/esm/Card";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import { synologyUrlProblem } from "../utils/synologyUrl";
import GemeindeProfileForm from "./GemeindeProfileForm";

// Choir-reminder fields stored in gemeinden/{id}/config/notificationSettings (GOD-2).
interface ChoirConfig {
    choirReminderEnabled: boolean;
    choirConductorName: string;
    choirConductorEmail: string;
    choirConductorPhone: string;
    choirSongCount: number;
    // Service types the choir sings at.
    choirServiceTypes: string[];
    // Published Google Sheet the weekly sync reads the songs from.
    choirSheetUrl: string;
    // A song sung within this many weeks gets a repeat warning.
    choirRepeatWarningWeeks: number;
}

const CHOIR_SERVICE_TYPE_OPTIONS = ["sunday-morning", "sunday-afternoon", "saturday", "wednesday", "weekday-special"];

const DEFAULT_CHOIR_CONFIG: ChoirConfig = {
    choirReminderEnabled: false,
    choirConductorName: "",
    choirConductorEmail: "",
    choirConductorPhone: "",
    choirSongCount: 4,
    choirServiceTypes: ["sunday-morning"],
    choirSheetUrl: "",
    choirRepeatWarningWeeks: 8,
};

// Synology recording-sync fields stored in gemeinden/{id}/config/notificationSettings (GOD-4).
interface SynologyConfig {
    synologySyncEnabled: boolean;
    synologyBaseUrl: string;
    synologyUsername: string;
    synologyPassword: string;
    synologyRecordingsPath: string;
}

const DEFAULT_SYNOLOGY_CONFIG: SynologyConfig = {
    synologySyncEnabled: false,
    synologyBaseUrl: "",
    synologyUsername: "",
    synologyPassword: "",
    synologyRecordingsPath: "",
};

/**
 * Gemeinde admin settings: shows the Gemeinde profile and released channels
 * (read-only – the superadmin controls release), and lets the admin set the
 * recording-page password (handled server-side; never stored in the document).
 */
export default function GemeindeSettings({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const { canEdit, loading } = useAuth();
    const { activeGemeinde, activeGemeindeId, refreshGemeinde } = useGemeinde();
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [choir, setChoir] = useState<ChoirConfig>(DEFAULT_CHOIR_CONFIG);
    const [savingChoir, setSavingChoir] = useState(false);
    // The conductor's personal link (/chor/<token>): null until known.
    const [conductorAccess, setConductorAccess] = useState<boolean | null>(null);
    const [conductorBusy, setConductorBusy] = useState(false);
    const [confirmRevoke, setConfirmRevoke] = useState(false);
    const [synology, setSynology] = useState<SynologyConfig>(DEFAULT_SYNOLOGY_CONFIG);
    const [savingSynology, setSavingSynology] = useState(false);
    // The Synology password is write-only: the page only knows whether one is set.
    const [hasSynologyPassword, setHasSynologyPassword] = useState(false);

    function notify(variant: string, msg: string) {
        setVariant(variant);
        setMessage(msg);
        setShowMessage(true);
    }

    const fallbackSheetUrl = activeGemeinde?.choirSheetUrl ?? "";

    // Load the choir-reminder config from the per-Gemeinde notification-settings doc.
    useEffect(() => {
        if (!activeGemeindeId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await loadGemeindeSettings(activeGemeindeId);
                if (cancelled) return;
                setChoir({
                    choirReminderEnabled: !!data.choirReminderEnabled,
                    choirConductorName: data.choirConductorName ?? "",
                    choirConductorEmail: data.choirConductorEmail ?? "",
                    choirConductorPhone: data.choirConductorPhone ?? "",
                    choirSongCount: typeof data.choirSongCount === "number" ? data.choirSongCount : 4,
                    choirServiceTypes:
                        Array.isArray(data.choirServiceTypes) && data.choirServiceTypes.length > 0
                            ? data.choirServiceTypes
                            : ["sunday-morning"],
                    // Until it is saved here, the value the superadmin stored on the Gemeinde applies.
                    choirSheetUrl: data.choirSheetUrl ?? fallbackSheetUrl,
                    choirRepeatWarningWeeks:
                        typeof data.choirRepeatWarningWeeks === "number" ? data.choirRepeatWarningWeeks : 8,
                });
                setSynology({
                    synologySyncEnabled: !!data.synologySyncEnabled,
                    synologyBaseUrl: data.synologyBaseUrl ?? "",
                    synologyUsername: data.synologyUsername ?? "",
                    synologyPassword: "",
                    synologyRecordingsPath: data.synologyRecordingsPath ?? "",
                });
                setHasSynologyPassword(!!data.hasSynologyPassword || !!data.synologyPassword);
            } catch (_) {
                /* keep defaults on read failure */
            }
        })();
        return () => { cancelled = true; };
    }, [activeGemeindeId, fallbackSheetUrl]);

    useEffect(() => {
        if (!activeGemeindeId || !canEdit) return;
        let cancelled = false;
        httpsCallable<{ gemeindeId: string }, { active: boolean }>(functions, "getConductorAccessStatus")({ gemeindeId: activeGemeindeId })
            .then((result) => !cancelled && setConductorAccess(result.data.active))
            .catch(() => undefined);
        return () => { cancelled = true; };
    }, [activeGemeindeId, canEdit]);

    async function handleSendConductorLink() {
        if (!activeGemeindeId) return;
        setConductorBusy(true);
        try {
            const result = await httpsCallable<
                { gemeindeId: string },
                { sent: boolean; toRepresentative?: boolean; reason?: "email-disabled" | "no-email" }
            >(functions, "sendConductorAccessLink")({ gemeindeId: activeGemeindeId });
            if (result.data.sent) {
                setConductorAccess(true);
                notify("success", t(result.data.toRepresentative ? "gemeinde.conductorLinkSentRepresentative" : "gemeinde.conductorLinkSent"));
            } else {
                notify("warning", t(result.data.reason === "no-email" ? "gemeinde.conductorLinkNoEmail" : "gemeinde.conductorLinkEmailDisabled"));
            }
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setConductorBusy(false);
        }
    }

    async function handleRevokeConductorLink() {
        if (!activeGemeindeId) return;
        setConductorBusy(true);
        try {
            await httpsCallable(functions, "revokeConductorAccess")({ gemeindeId: activeGemeindeId });
            setConductorAccess(false);
            setConfirmRevoke(false);
            notify("success", t("gemeinde.conductorLinkRevoked"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setConductorBusy(false);
        }
    }

    async function handleSaveChoir(e: React.FormEvent) {
        e.preventDefault();
        if (!activeGemeindeId) return;
        setSavingChoir(true);
        try {
            // Merged: only the choir fields, the preacher notification settings in
            // this doc stay untouched.
            await saveGemeindeSettings(activeGemeindeId, {
                choirReminderEnabled: choir.choirReminderEnabled,
                choirConductorName: choir.choirConductorName.trim(),
                choirConductorEmail: choir.choirConductorEmail.trim(),
                choirConductorPhone: choir.choirConductorPhone.trim(),
                choirSongCount: Math.min(4, Math.max(1, choir.choirSongCount)),
                choirServiceTypes: choir.choirServiceTypes.length > 0 ? choir.choirServiceTypes : ["sunday-morning"],
                choirSheetUrl: choir.choirSheetUrl.trim(),
                choirRepeatWarningWeeks: Math.min(52, Math.max(0, Math.round(choir.choirRepeatWarningWeeks) || 0)),
            });
            notify("success", t("gemeinde.choirSaved"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setSavingChoir(false);
        }
    }

    async function handleSaveSynology(e: React.FormEvent) {
        e.preventDefault();
        if (!activeGemeindeId) return;
        // The functions only connect to public https addresses (GOD-51); say so before saving.
        const urlProblem = synologyUrlProblem(synology.synologyBaseUrl);
        if (urlProblem) {
            setVariant("warning");
            setMessage(t(`gemeinde.synologyUrlProblems.${urlProblem}`));
            setShowMessage(true);
            return;
        }
        setSavingSynology(true);
        try {
            // Merged: only the Synology fields in this doc.
            await saveGemeindeSettings(activeGemeindeId, {
                synologySyncEnabled: synology.synologySyncEnabled,
                synologyBaseUrl: synology.synologyBaseUrl.trim(),
                synologyUsername: synology.synologyUsername.trim(),
                synologyRecordingsPath: synology.synologyRecordingsPath.trim(),
            });
            // A new password goes through a callable that stores it encrypted;
            // an empty field keeps the stored one.
            if (synology.synologyPassword) {
                await httpsCallable(functions, "saveSynologyPassword")({
                    gemeindeId: activeGemeindeId,
                    password: synology.synologyPassword,
                });
                setSynology({ ...synology, synologyPassword: "" });
                setHasSynologyPassword(true);
            }
            notify("success", t("gemeinde.synologySaved"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setSavingSynology(false);
        }
    }

    async function handleSetPassword(e: React.FormEvent) {
        e.preventDefault();
        if (!activeGemeindeId) return;
        setBusy(true);
        try {
            const setRecordingPassword = httpsCallable(functions, "setRecordingPassword");
            await setRecordingPassword({ gemeindeId: activeGemeindeId, password });
            setPassword("");
            refreshGemeinde();
            notify("success", t("gemeinde.recordingPasswordSaved"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setBusy(false);
        }
    }

    if (loading) {
        return <div className="text-center" style={{ padding: "3rem" }}><Spinner animation="border" /></div>;
    }
    if (!canEdit) {
        return <div className="gl-content"><Alert variant="danger">{t("admin.notAuthorized")}</Alert></div>;
    }
    if (!activeGemeindeId || !activeGemeinde) {
        return <div className="gl-content"><Alert variant="info">{t("gemeinde.selectFirst")}</Alert></div>;
    }

    const channelLabel = (released: boolean) =>
        released ? t("gemeinde.channelReleased") : t("gemeinde.channelNotReleased");

    return (
        <div className="gl-content">
            <div className="mb-4">
                <div className="gl-meta mb-1">{t("gemeinde.settings")}</div>
                <h1 className="mb-0">{activeGemeinde.name}</h1>
            </div>

            <Card className="mb-4">
                <Card.Body>
                    <h4>{t("gemeinde.profile")}</h4>
                    <dl className="row mb-3">
                        <dt className="col-sm-3">{t("superadmin.slug")}</dt>
                        <dd className="col-sm-9"><code>{activeGemeinde.slug}</code></dd>
                        <dt className="col-sm-3">{t("gemeinde.channels")}</dt>
                        <dd className="col-sm-9">
                            E-Mail: {channelLabel(activeGemeinde.channels.emailReleased)} · WhatsApp: {channelLabel(activeGemeinde.channels.whatsappReleased)} · Push: {channelLabel(activeGemeinde.channels.pushReleased)}
                        </dd>
                    </dl>
                    <GemeindeProfileForm gemeinde={activeGemeinde} onSaved={refreshGemeinde} notify={notify} />
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <h4>{t("gemeinde.recordingPassword")}</h4>
                    <p className="text-muted">
                        {t("gemeinde.recordingPasswordHint")}{" "}
                        {activeGemeinde.hasRecordingPassword ? t("gemeinde.recordingPasswordSet") : t("gemeinde.recordingPasswordNotSet")}
                    </p>
                    <Form onSubmit={handleSetPassword} className="d-flex gap-2 align-items-end" style={{ maxWidth: 480 }}>
                        <Form.Group className="flex-grow-1">
                            <Form.Label>{t("gemeinde.newPassword")}</Form.Label>
                            <Form.Control type="text" value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder={t("gemeinde.recordingPasswordPlaceholder") as string} />
                        </Form.Group>
                        <Button type="submit" disabled={busy}>
                            {t("admin.save")} {busy && <Spinner size="sm" animation="grow" as="span" />}
                        </Button>
                    </Form>
                    <Form.Text className="text-muted">{t("gemeinde.recordingPasswordClear")}</Form.Text>
                </Card.Body>
            </Card>

            <Card className="mt-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h4 className="mb-0">{t("gemeinde.choirReminder")}</h4>
                        <Form.Check
                            type="switch"
                            id="choirReminderEnabled"
                            label={t("gemeinde.choirReminderEnabled")}
                            checked={choir.choirReminderEnabled}
                            onChange={(e) => setChoir({ ...choir, choirReminderEnabled: e.target.checked })}
                        />
                    </div>
                    <p className="text-muted">{t("gemeinde.choirReminderHint")}</p>
                    <Form onSubmit={handleSaveChoir}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.choirConductorName")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={choir.choirConductorName}
                                        onChange={(e) => setChoir({ ...choir, choirConductorName: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.choirConductorEmail")}</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={choir.choirConductorEmail}
                                        onChange={(e) => setChoir({ ...choir, choirConductorEmail: e.target.value })}
                                        placeholder="dirigent@example.com"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.choirConductorPhone")}</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        value={choir.choirConductorPhone}
                                        onChange={(e) => setChoir({ ...choir, choirConductorPhone: e.target.value })}
                                        placeholder="+49…"
                                    />
                                    <Form.Text className="text-muted">{t("gemeinde.choirConductorPhoneHint")}</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.choirSongCount")}</Form.Label>
                                    <Form.Select
                                        value={choir.choirSongCount}
                                        onChange={(e) => setChoir({ ...choir, choirSongCount: parseInt(e.target.value, 10) || 4 })}
                                    >
                                        {[1, 2, 3, 4].map((n) => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("gemeinde.choirServiceTypes")}</Form.Label>
                            <div>
                                {CHOIR_SERVICE_TYPE_OPTIONS.map((type) => (
                                    <Form.Check
                                        inline
                                        key={type}
                                        type="checkbox"
                                        id={`choirServiceType-${type}`}
                                        label={t(`serviceTypes.${type}`)}
                                        checked={choir.choirServiceTypes.includes(type)}
                                        onChange={(e) =>
                                            setChoir({
                                                ...choir,
                                                choirServiceTypes: e.target.checked
                                                    ? [...choir.choirServiceTypes, type]
                                                    : choir.choirServiceTypes.filter((other) => other !== type),
                                            })
                                        }
                                    />
                                ))}
                            </div>
                            <Form.Text className="text-muted">{t("gemeinde.choirServiceTypesHint")}</Form.Text>
                        </Form.Group>
                        <Row className="mb-3">
                            <Col md={8}>
                                <Form.Group controlId="choirSheetUrl">
                                    <Form.Label>{t("gemeinde.choirSheetUrl")}</Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={choir.choirSheetUrl}
                                        onChange={(e) => setChoir({ ...choir, choirSheetUrl: e.target.value })}
                                        placeholder="https://docs.google.com/spreadsheets/d/e/…/pub?output=csv"
                                        pattern="https://docs\.google\.com/spreadsheets/.*"
                                    />
                                    <Form.Text className="text-muted">{t("gemeinde.choirSheetUrlHint")}</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="choirRepeatWarningWeeks">
                                    <Form.Label>{t("gemeinde.choirRepeatWarningWeeks")}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        max={52}
                                        value={choir.choirRepeatWarningWeeks}
                                        onChange={(e) => setChoir({ ...choir, choirRepeatWarningWeeks: parseInt(e.target.value, 10) || 0 })}
                                    />
                                    <Form.Text className="text-muted">{t("gemeinde.choirRepeatWarningWeeksHint")}</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Button type="submit" disabled={savingChoir}>
                            {t("admin.save")} {savingChoir && <Spinner size="sm" animation="grow" as="span" />}
                        </Button>
                    </Form>

                    <hr />
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <h5 className="mb-0">{t("gemeinde.conductorAccess")}</h5>
                        {conductorAccess !== null && (
                            <span className={`gl-badge ${conductorAccess ? "gl-badge--ok" : "gl-badge--warn"}`}>
                                {t(conductorAccess ? "gemeinde.conductorAccessActive" : "gemeinde.conductorAccessNone")}
                            </span>
                        )}
                    </div>
                    <p className="text-muted">
                        {t("gemeinde.conductorAccessHint", { url: `${window.location.host}${Globals.PATH_CHOIR_PLAN}` })}
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                        <Button variant="outline-primary" onClick={handleSendConductorLink} disabled={conductorBusy}>
                            {t("gemeinde.conductorLinkSend")}
                        </Button>
                        {conductorAccess &&
                            (confirmRevoke ? (
                                <Button variant="danger" onClick={handleRevokeConductorLink} disabled={conductorBusy}>
                                    {t("gemeinde.conductorLinkRevokeConfirm")}
                                </Button>
                            ) : (
                                <Button variant="outline-danger" onClick={() => setConfirmRevoke(true)} disabled={conductorBusy}>
                                    {t("gemeinde.conductorLinkRevoke")}
                                </Button>
                            ))}
                    </div>
                </Card.Body>
            </Card>

            <Card className="mt-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h4 className="mb-0">{t("gemeinde.synologySync")}</h4>
                        <Form.Check
                            type="switch"
                            id="synologySyncEnabled"
                            label={t("gemeinde.synologySyncEnabled")}
                            checked={synology.synologySyncEnabled}
                            onChange={(e) => setSynology({ ...synology, synologySyncEnabled: e.target.checked })}
                        />
                    </div>
                    <p className="text-muted">{t("gemeinde.synologySyncHint")}</p>
                    <Form onSubmit={handleSaveSynology}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.synologyBaseUrl")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={synology.synologyBaseUrl}
                                        onChange={(e) => setSynology({ ...synology, synologyBaseUrl: e.target.value })}
                                        placeholder="https://nas.example.com:5001"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.synologyRecordingsPath")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={synology.synologyRecordingsPath}
                                        onChange={(e) => setSynology({ ...synology, synologyRecordingsPath: e.target.value })}
                                        placeholder="/music/Aufnahmen"
                                    />
                                    <Form.Text className="text-muted">{t("gemeinde.synologyRecordingsPathHint")}</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.synologyUsername")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={synology.synologyUsername}
                                        onChange={(e) => setSynology({ ...synology, synologyUsername: e.target.value })}
                                        autoComplete="off"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("gemeinde.synologyPassword")}</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={synology.synologyPassword}
                                        onChange={(e) => setSynology({ ...synology, synologyPassword: e.target.value })}
                                        placeholder={hasSynologyPassword ? (t("gemeinde.synologyPasswordSet") as string) : ""}
                                        autoComplete="new-password"
                                    />
                                    <Form.Text className="text-muted">{t("gemeinde.synologyPasswordHint")}</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Button type="submit" disabled={savingSynology}>
                            {t("admin.save")} {savingSynology && <Spinner size="sm" animation="grow" as="span" />}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
}
